import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Phone } from 'lucide-react';
import useFocusTrap from '../../hooks/useFocusTrap';
import { LANGUAGES, PHONE_HREF, PHONE_NUMBER } from '../../siteConfig';

const NAV_ITEMS = [
  { label: 'site.nav.home', to: '/' },
  { label: 'site.nav.how', to: '/#how' },
  { label: 'site.nav.services', to: '/services' },
  { label: 'navbar.rates', to: '/rates' },
  { label: 'navbar.about', to: '/about' },
  { label: 'site.nav.contact', to: '#contact' },
];

function SiteHeader() {
  const { t, i18n } = useTranslation();
  const { pathname, hash } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const drawerRef = useFocusTrap(open, () => setOpen(false));
  const topRef = useRef(null);

  // The header only shows at the very top of the page: as soon as the
  // visitor starts scrolling (40px) it slides away, on every page.
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setScrolled(!entry.isIntersecting));
    if (topRef.current) observer.observe(topRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => setOpen(false), [pathname, hash]);

  const isCurrent = (to) => (to === '/' ? pathname === '/' && !hash : `${pathname}${hash}` === to);

  const navLink = (item, className) => (
    <Link
      key={item.to}
      to={item.to}
      aria-current={isCurrent(item.to) ? 'page' : undefined}
      className={className}
      onClick={() => setOpen(false)}
    >
      {t(item.label)}
    </Link>
  );

  const hidden = scrolled && !open;

  return (
    <>
      <div ref={topRef} aria-hidden="true" className="pointer-events-none absolute left-0 top-0 h-10 w-px" />
      <header
        className={`sticky top-0 z-header border-0 border-b border-solid border-white/10 bg-navy font-sans transition-transform duration-[250ms] ease-out motion-reduce:transition-none ${
          hidden ? '-translate-y-full has-[:focus-visible]:translate-y-0' : 'translate-y-0'
        }`}
      >
        <div className="site-container flex h-16 items-center justify-between gap-3 lg:h-[72px] lg:gap-6">
          <Link to="/" className="focus-ring-dark flex shrink-0 items-center gap-2.5 rounded-btn text-white no-underline" aria-label="TaxiReady2Go">
            <img src="/logo-mark.png" alt="" width="44" height="36" className="h-9 w-auto" />
            <span className="text-base font-extrabold tracking-tight max-[369px]:hidden lg:text-lg">TaxiReady2Go</span>
          </Link>

          <nav aria-label={t('site.nav.label')} className="hidden lg:block">
            <ul className="m-0 flex list-none items-center gap-1 p-0">
              {NAV_ITEMS.map((item) => (
                <li key={item.to}>
                  {navLink(
                    item,
                    'focus-ring-dark rounded-btn px-3 py-2 text-[15px] font-semibold text-slate-300 no-underline transition-colors duration-150 hover:text-white aria-[current=page]:text-taxi'
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
          <label className="flex items-center">
            <span className="sr-only">
              {t('site.widgets.language', { language: (LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) || LANGUAGES[0]).name })}
            </span>
            <select
              value={i18n.resolvedLanguage}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
              className="focus-ring-dark h-10 cursor-pointer rounded-full border border-solid border-white/20 bg-navy px-3 font-sans text-sm font-bold text-white hover:border-white/40"
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code} lang={lang.code}>
                  {lang.code.toUpperCase()}
                </option>
              ))}
            </select>
          </label>

          <Link to="/#book" className="btn-primary focus-ring-dark px-5">
            {t('site.nav.bookNow')}
          </Link>
          </div>

          <div className="flex items-center gap-1.5 lg:hidden">
            <a
              href={PHONE_HREF}
              aria-label={t('site.nav.call', { phone: PHONE_NUMBER })}
              className="focus-ring-dark inline-flex h-10 w-10 items-center justify-center rounded-full border border-solid border-white/20 text-white transition-colors duration-150 hover:bg-white/10"
            >
              <Phone size={18} aria-hidden="true" />
            </a>
            <Link to="/#book" className="btn-primary focus-ring-dark min-h-[40px] rounded-full px-4 py-2 text-sm">
              {t('site.nav.bookShort')}
            </Link>
            <button
              type="button"
              className="focus-ring-dark -mr-2 inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-btn border-0 bg-transparent text-white"
              aria-label={t('site.nav.openMenu')}
              aria-expanded={open}
              aria-controls="site-drawer"
              onClick={() => setOpen(true)}
            >
              <Menu size={26} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {createPortal(
        <>
          <div
            className={`fixed inset-0 z-overlay bg-navy-deep/70 transition-opacity duration-200 motion-reduce:transition-none lg:hidden ${
              open ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
            aria-hidden="true"
            onClick={() => setOpen(false)}
          />
          <div
            id="site-drawer"
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('site.nav.menu')}
            tabIndex={-1}
            {...(open ? {} : { inert: '' })}
            className={`fixed inset-y-0 right-0 z-overlay flex w-[min(86vw,360px)] flex-col bg-navy-deep px-6 pb-8 pt-4 font-sans shadow-2xl transition-transform duration-300 ease-out motion-reduce:transition-none lg:hidden ${
              open ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="flex h-12 items-center justify-between">
              <span className="text-lg font-extrabold text-white">TaxiReady2Go</span>
              <button
                type="button"
                className="focus-ring-dark -mr-2 inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-btn border-0 bg-transparent text-white"
                aria-label={t('site.nav.closeMenu')}
                onClick={() => setOpen(false)}
              >
                <X size={26} aria-hidden="true" />
              </button>
            </div>
            <nav aria-label={t('site.nav.label')} className="mt-6">
              <ul className="m-0 flex list-none flex-col gap-1 p-0">
                {NAV_ITEMS.map((item) => (
                  <li key={item.to}>
                    {navLink(
                      item,
                      'focus-ring-dark flex min-h-[48px] items-center rounded-btn px-3 text-lg font-semibold text-white no-underline hover:bg-white/5 aria-[current=page]:text-taxi'
                    )}
                  </li>
                ))}
              </ul>
            </nav>
            <div
              role="group"
              aria-label={t('site.widgets.language', { language: (LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) || LANGUAGES[0]).name })}
              className="mt-6 flex flex-wrap gap-1.5 px-3"
            >
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  lang={lang.code}
                  title={lang.name}
                  aria-label={lang.name}
                  aria-pressed={i18n.resolvedLanguage === lang.code}
                  onClick={() => i18n.changeLanguage(lang.code)}
                  className="focus-ring-dark inline-flex h-11 min-w-[44px] cursor-pointer items-center justify-center rounded-full border border-solid border-white/20 bg-transparent px-3 text-sm font-bold text-white transition-colors duration-150 hover:bg-white/10 aria-pressed:border-taxi aria-pressed:bg-taxi aria-pressed:text-navy"
                >
                  {lang.code.toUpperCase()}
                </button>
              ))}
            </div>
            <div className="mt-auto flex flex-col gap-4">
              <Link to="/#book" className="btn-primary focus-ring-dark w-full" onClick={() => setOpen(false)}>
                {t('site.nav.bookNow')}
              </Link>
              <a
                href={PHONE_HREF}
                className="focus-ring-dark flex min-h-[44px] items-center justify-center gap-2 rounded-btn font-semibold text-slate-300 no-underline hover:text-white"
              >
                <Phone size={18} aria-hidden="true" />
                {PHONE_NUMBER}
              </a>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

export default SiteHeader;
